// ============================================================
// ThreatCodex - the scan's other face (v8.270.0).
//
// Clicking the denomination flips the Scan tab's map cell into
// this dataslate: the composed designation, the seven-rung tier
// ladder (verdict lit, next rung filled by in-band progress),
// the five vector bars (segmented, Roman-numbered, each carrying
// its BASIS derivation as a hover title - the instrument shows
// its work), the full epithet set, the IMMUNIS row, and the KILL
// COUNSEL in High Gothic. INTERACTIVE, unlike the HUD - Esc or
// the footer control returns to the scan.
//
// Reads sheet facts only (wounds.MAX through the engine, never
// current wounds) - nothing here needs the bandedWounds gate.
// ============================================================
import { useCallback, useMemo, useRef } from 'react';
import clsx from '../utils/clsx';
import useEscapeKey from '../hooks/useEscapeKey.js';
import { classifyThreat, tierProgress, TIER_ORDER } from '../engine/threatAssessment';
import type { Target } from '../types';
import styles from '../styles/ThreatCodex.module.css';

interface Props {
  target: Target;
  sourceCharacter?: unknown;
  onClose: () => void;
}

const VECTOR_ORDER = ['ferocitas', 'duritia', 'terror', 'immaterium', 'corpus'] as const;
const ROMAN = ['0', 'I', 'II', 'III', 'IV', 'V'];

/** Deterministic ident line from the name - stable, no randomness. */
function identOf(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return `INQ/${h.toString(16).toUpperCase().slice(0, 4).padStart(4, '0')}-${(h % 9) + 1}`;
}

type Threat = ReturnType<typeof classifyThreat>;

export default function ThreatCodex({ target, sourceCharacter = null, onClose }: Props) {
  const threat = useMemo(() => classifyThreat(target, sourceCharacter as never), [target, sourceCharacter]);
  // Claimed on the document (useEscapeKey): on the window it came after the target panel's own Escape, which closed
  // the panel round the codex and left the codex up (2026-10-23). Only while it shows: the codex stays mounted in a
  // closed panel, and must not eat the Escape that cancels an attack.
  const ref = useRef<HTMLDivElement>(null);
  const showing = useCallback(() => !ref.current?.closest('[hidden]'), []);
  useEscapeKey(true, onClose, showing);
  const quote = (sourceCharacter as { codexQuote?: string } | null)?.codexQuote?.trim();
  return (
    <div ref={ref} className={styles.codex} data-testid="threat-codex">
      <div className={styles.masthead}>
        <span className={styles.eyebrow}>THREAT CODEX · AUSPEX CLASSIFICATION</span>
        <span className={styles.designation} data-testid="codex-designation">
          {threat.label}
        </span>
        <span className={styles.subject}>
          {target.name} <span className={styles.ident}>REF: {identOf(target.name || '')}</span>
        </span>
      </div>
      <TierLadder threat={threat} />
      <VectorBars threat={threat} />
      <CodexNotes threat={threat} />
      {/* The slate breathes: the tail sits at the foot. */}
      <div className={styles.spacer} aria-hidden="true" />
      {quote && (
        <div className={styles.epigraph} data-testid="codex-epigraph">
          {'\u201C'}
          {quote}
          {'\u201D'}
        </div>
      )}
      <button type="button" className={styles.close} data-testid="codex-close" onClick={onClose}>
        RETURN TO SCAN [ESC]
      </button>
    </div>
  );
}

/**
 * The seven-rung ladder: verdict lit, the NEXT rung part-filled by
 * in-band progress - two MAJORIS are visibly not equals.
 */
function TierLadder({ threat }: { threat: Threat }) {
  const progress = tierProgress(threat.total);
  const tierIndex = TIER_ORDER.indexOf(threat.tier);
  return (
    <div className={styles.ladder} data-testid="codex-ladder">
      {TIER_ORDER.map((tier, i) => (
        <span
          key={tier}
          className={clsx(styles.ladderRung, i < tierIndex && styles.rungPassed, i === tierIndex && styles.rungLit)}
          data-testid={`codex-tier-${tier.toLowerCase()}`}
        >
          <span
            className={styles.rungFill}
            style={i === tierIndex + 1 ? { width: `${Math.round(progress * 100)}%` } : undefined}
            aria-hidden="true"
          />
          {tier}
        </span>
      ))}
    </div>
  );
}

/** The five vector bars, segmented and Roman-numbered, each titled with its basis. */
function VectorBars({ threat }: { threat: Threat }) {
  return (
    <div className={styles.vectors} data-testid="codex-vectors">
      {VECTOR_ORDER.map((key) => (
        <div key={key} className={styles.vectorRow} title={threat.basis[key]} data-testid={`codex-vector-${key}`}>
          <span className={styles.vectorName}>
            {key.toUpperCase()}
            {key === 'ferocitas' && threat.estimated && (
              <span className={styles.estimate} data-testid="codex-estimate">
                ESTIMATE
              </span>
            )}
          </span>
          <span className={styles.vectorBar} aria-hidden="true">
            {[1, 2, 3, 4, 5].map((seg) => (
              <span key={seg} className={clsx(styles.segment, threat.vectors[key] >= seg && styles.segmentLit)} />
            ))}
          </span>
          <span className={styles.vectorScore}>{ROMAN[threat.vectors[key]]}</span>
        </div>
      ))}
    </div>
  );
}

/** The full epithet set, the IMMUNIS row and the KILL COUNSEL - each only when there is any. */
function CodexNotes({ threat }: { threat: Threat }) {
  return (
    <>
      {threat.epithets.length > 0 && (
        <div className={styles.epithets} data-testid="codex-epithets">
          {threat.epithets.map((e) => (
            <span key={e} className={clsx(styles.epithet, e.startsWith('FEAR') && styles.epithetDanger)}>
              {e}
            </span>
          ))}
        </div>
      )}
      {threat.immunities.length > 0 && (
        <div className={styles.immunis} data-testid="codex-immunis">
          <span className={styles.immunisLabel}>IMMUNIS:</span> {threat.immunities.join(' · ')}
        </div>
      )}
      {threat.counsel.length > 0 && (
        <div className={styles.counsel} data-testid="codex-counsel">
          {threat.counsel.map((line) => (
            <div key={line} className={styles.counselLine}>
              <span aria-hidden="true">{'▸ '}</span>
              {line}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
